import {
  Tab as ChakraTab,
  TabList,
  TabPanel,
  TabPanels,
  TabProps,
  Tabs,
  TabsProps,
  forwardRef,
} from "@chakra-ui/react";
import { isEqual } from "lodash";
import React from "react";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { ConfirmationDialogProps } from "../Dialogs/ConfirmationDialog";
import { DefaultUnsavedChangesConfirmationDialogProps } from "../NavigationBlocker/constants";

/**
 * Extended Tab component props that includes hashKey
 */
export interface TabWithKeyProps extends TabProps {
  hashKey: string;
}

/**
 * Custom Tab component that accepts hashKey prop but renders as Chakra Tab
 */
export const Tab = forwardRef<TabWithKeyProps, "button">(
  ({ hashKey, ...props }, ref) => {
    return <ChakraTab {...props} ref={ref} />;
  }
);
Tab.displayName = "Tab";

// Re-export Chakra UI components for convenience
export { TabList, TabPanel, TabPanels };

/**
 * Props for TabsWithHash component.
 * The component automatically extracts tab keys from Tab components within TabList.
 * Tab components should have a hashKey prop for identification.
 *
 * @example
 * <TabsWithHash>
 *   <TabList>
 *     <Tab hashKey="overview">Overview</Tab>
 *     <Tab hashKey="details">Details</Tab>
 *   </TabList>
 *   <TabPanels>
 *     <TabPanel>Overview content</TabPanel>
 *     <TabPanel>Details content</TabPanel>
 *   </TabPanels>
 * </TabsWithHash>
 */
export interface TabsWithHashProps
  extends Omit<TabsProps, "index" | "onChange"> {
  children: React.ReactNode;
}

type PartialConfirmationDialogProps = Partial<
  Omit<ConfirmationDialogProps, "onCancel" | "onConfirm" | "isOpen">
>;
export const TabsWithHash = forwardRef<TabsWithHashProps, "div">(
  ({ children, ...props }, ref) => {
    const hashKeys = extractHashKeysFromChildren(children);
    const [tabIndex, setTabIndex] = useHashTab(hashKeys);
    const [isDirty, setIsDirty] = React.useState(false);
    const [confirmationDialogProps, setConfirmationDialogProps] =
      React.useState<PartialConfirmationDialogProps>({});

    const confirm = useConfirmationDialog({
      ...DefaultUnsavedChangesConfirmationDialogProps,
      ...confirmationDialogProps,
    });

    return (
      <TabsWithHashContext.Provider
        value={{
          setIsDirty,
          setConfirmationDialogProps,
          confirmationDialogProps,
        }}
      >
        <Tabs
          {...props}
          ref={ref}
          index={tabIndex}
          onChange={async (number) => {
            if (
              isDirty &&
              !(await confirm.confirm({
                ...confirmationDialogProps,
              }))
            ) {
              return;
            }

            setIsDirty(false);
            setTabIndex(number);
          }}
        >
          {children}
        </Tabs>
        {confirm.confirmationDialog}
      </TabsWithHashContext.Provider>
    );
  }
);

type TabsWithHashContextType = {
  setIsDirty: (isDirty: boolean) => void;
  setConfirmationDialogProps: (props: PartialConfirmationDialogProps) => void;
  confirmationDialogProps: PartialConfirmationDialogProps;
};

export const TabsWithHashContext = React.createContext<TabsWithHashContextType>(
  {
    setIsDirty: () => {},
    setConfirmationDialogProps: () => {},
    confirmationDialogProps: {},
  }
);

export function useTabsWithHashBlocker(
  hasChanges: boolean,
  props?: PartialConfirmationDialogProps
) {
  const { setIsDirty, setConfirmationDialogProps, confirmationDialogProps } =
    React.useContext(TabsWithHashContext);
  React.useEffect(() => {
    setIsDirty(hasChanges);
    return () => {
      // reset isDirty on unmount
      setIsDirty(false);
    };
  }, [hasChanges, setIsDirty]);

  React.useEffect(() => {
    if (props && !isEqual(props, confirmationDialogProps)) {
      setConfirmationDialogProps(props);
    }
  }, [confirmationDialogProps, props, setConfirmationDialogProps]);
}

/**
 * Extracts hash keys from Tab components within TabList children.
 * Looks for hashKey props on Tab components.
 * Falls back to auto-generated keys if none found.
 */
const extractHashKeysFromChildren = (children: React.ReactNode): string[] => {
  const hashKeys: string[] = [];

  const extractFromNode = (node: React.ReactNode) => {
    React.Children.forEach(node, (child) => {
      if (React.isValidElement(child)) {
        const isTabComponent = (child.type as any)?.displayName === "Tab";

        if (isTabComponent) {
          const hashKey = child.props?.hashKey || `tab-${hashKeys.length}`;
          hashKeys.push(hashKey);
        }

        if (child.props && child.props.children) {
          extractFromNode(child.props.children);
        }
      }
    });
  };

  extractFromNode(children);
  return hashKeys;
};

const useHashTab = (hashKeys: readonly string[]) => {
  // Get initial index from hash or default to 0
  const getInitialIndex = React.useCallback(() => {
    const hash = window.location.hash.replace("#", "");
    if (!hashKeys || hashKeys.length === 0) return parseInt(hash) || 0;

    const index = hashKeys.indexOf(hash);
    return index >= 0 ? index : 0;
  }, [hashKeys]);

  const [tabIndex, setTabIndex] = React.useState(getInitialIndex);
  const hasMountedRef = React.useRef(false);
  const prevTabIndexRef = React.useRef(tabIndex);

  // Sync tabIndex -> hash.  hashKeys is a new array every render
  // (extractHashKeysFromChildren allocates fresh) so this effect would fire
  // unconditionally if we wrote the hash on every invocation.  An
  // unconditional write overwrites any hash set externally (e.g. by a link
  // navigating to a dynamically-shown tab) before the hashchange listener has
  // had a chance to call setTabIndex.  Guard: write on mount (to establish
  // the initial hash) and thereafter only when tabIndex has actually changed.
  React.useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      const hash = hashKeys[tabIndex] ?? tabIndex.toString();
      window.location.hash = hash;
      return;
    }
    if (tabIndex !== prevTabIndexRef.current) {
      prevTabIndexRef.current = tabIndex;
      const hash = hashKeys[tabIndex] ?? tabIndex.toString();
      window.location.hash = hash;
    }
  }, [tabIndex, hashKeys]);

  // Listen to hash changes
  React.useEffect(() => {
    const handleHashChange = () => {
      const newIndex = getInitialIndex();
      setTabIndex(newIndex);
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [getInitialIndex, hashKeys, setTabIndex]);

  return [tabIndex, setTabIndex] as const;
};
